import {
    BusinessProfileProgress,
    businessProfileProgress,
    businessProfileSteps,
    completedBusinessProfileSteps,
} from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { Badge, BadgeProps } from "components/ui/badge";
import { PrimaryButtonLink, SecondaryButtonLink } from "components/ui/button-link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import React from "react";
import {
    businessDisplayName,
    businessProfilePath,
    toBusinessProfileDetails,
} from "./business-profile-types";
import { BusinessProfileOutput } from "./use-business-verification";

const sectionCount = businessProfileSteps.length;

const cardClasses = "rounded-lg border border-gray-200 p-4 space-y-4";

export interface ProgressBadgeStyle {
    label: string;
    type: BadgeProps["type"];
}

export const progressBadges: Record<BusinessProfileProgress, ProgressBadgeStyle> = {
    inProgress: { label: "Draft", type: "info" },
    readyForReview: { label: "Ready for review", type: "success" },
    submitted: { label: "Submitted", type: "highlight" },
};

export const ProgressBadge: React.FC<{ progress: BusinessProfileProgress }> = props => (
    <Badge
        value={progressBadges[props.progress].label}
        type={progressBadges[props.progress].type}
    />
);

const submittedText =
    "Our team sent this business for verification. We'll let you know when carriers have reviewed it.";

export const readyForReviewText =
    "All sections are done. Our team will review your business profile before we send it for verification.";

const progressText = (progress: BusinessProfileProgress, done: number): string => {
    switch (progress) {
        case "inProgress":
            return `You've started. ${done} of ${sectionCount} sections are done, and your answers are saved.`;
        case "readyForReview":
            return readyForReviewText;
        case "submitted":
            return submittedText;
    }
    const exhaustiveCheck: never = progress;
    throw new Error(`Unhandled business profile progress: ${exhaustiveCheck}`);
};

const viewerProgressText = (progress: BusinessProfileProgress, done: number): string => {
    switch (progress) {
        case "inProgress":
            return `This business is in progress. ${done} of ${sectionCount} sections are done.`;
        case "readyForReview":
            return "All sections are done. Our team will review this business profile before we send it for verification.";
        case "submitted":
            return submittedText;
    }
    const exhaustiveCheck: never = progress;
    throw new Error(`Unhandled business profile progress: ${exhaustiveCheck}`);
};

interface CardActionProps {
    profile: BusinessProfileOutput;
    progress: BusinessProfileProgress;
}

const CardAction: React.FC<CardActionProps> = ({ profile, progress }) => {
    switch (progress) {
        case "inProgress":
            return (
                <PrimaryButtonLink to={businessProfilePath(profile.id)}>
                    Continue
                </PrimaryButtonLink>
            );
        case "readyForReview":
            return (
                <SecondaryButtonLink to={businessProfilePath(profile.id)}>
                    Edit profile
                </SecondaryButtonLink>
            );
        case "submitted":
            return null;
    }
    const exhaustiveCheck: never = progress;
    throw new Error(`Unhandled business profile progress: ${exhaustiveCheck}`);
};

export const WhatToHaveOnHand: React.FC = () => (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
        <p className="text-sm font-medium text-gray-700">What to have on hand</p>
        <ul className="mt-2 space-y-1.5 text-sm text-gray-700 list-disc pl-5">
            <li>The IRS letter that shows your EIN (form CP 575 or 147C)</li>
            <li>Your business street address. Carriers don&apos;t accept PO boxes.</li>
            <li>Your website</li>
            <li>The name, title, email, and phone number of an owner or officer</li>
        </ul>
        <p className="mt-3 text-sm text-gray-500">
            It takes a few minutes, and you can stop and come back. No EIN?{" "}
            <SupportEmailLink
                label="Talk to us"
                subject="SMS & Voice Business Verification without an EIN"
            />{" "}
            before you start.
        </p>
    </div>
);

interface BusinessCardProps {
    profile: BusinessProfileOutput;
    isMultiOffice: boolean;
    coveredOffices: (officeIds: BusinessProfileOutput["officeIds"]) => string;
}

export const BusinessCard: React.FC<BusinessCardProps> = props => {
    const { profile, isMultiOffice, coveredOffices } = props;
    const details = toBusinessProfileDetails(profile);
    const done = completedBusinessProfileSteps(details).length;
    const progress = businessProfileProgress(profile.status, details);

    return (
        <div className={cardClasses}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">
                            {businessDisplayName(details)}
                        </span>
                        <ProgressBadge progress={progress} />
                    </div>
                    {isMultiOffice ? (
                        <p className="text-sm text-gray-500 mt-0.5">
                            Covers {coveredOffices(profile.officeIds)}
                        </p>
                    ) : null}
                    <p className="text-sm text-gray-700 mt-2 max-w-2xl">
                        {profile.editable
                            ? progressText(progress, done)
                            : viewerProgressText(progress, done)}
                    </p>
                    {profile.editable ? null : (
                        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                            Only someone who manages every office it covers can change it.
                        </p>
                    )}
                </div>
                {profile.editable ? (
                    <div className="shrink-0">
                        <CardAction profile={profile} progress={progress} />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

interface NotStartedCardProps {
    busy: boolean;
    onStart: () => void;
}

export const NotStartedCard: React.FC<NotStartedCardProps> = ({ busy, onStart }) => (
    <div className={cardClasses}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900">Your business</span>
                    <Badge value="Not started" type="info" />
                </div>
                <p className="text-sm text-gray-700 mt-2 max-w-2xl">
                    Carriers now require every business to be verified. Until you are,
                    your calls may show up as spam and some of your texts may not arrive.
                </p>
            </div>
            <div className="shrink-0">
                <PrimaryButton type="button" disabled={busy} onClick={onStart}>
                    Verify your business
                </PrimaryButton>
            </div>
        </div>
        <WhatToHaveOnHand />
    </div>
);

interface UncoveredOfficeCardProps {
    officeName: string;
    joinableBusinesses: BusinessProfileOutput[];
    busy: boolean;
    onJoin: (profile: BusinessProfileOutput) => void;
    onStart: () => void;
}

export const UncoveredOfficeCard: React.FC<UncoveredOfficeCardProps> = props => {
    const { officeName, joinableBusinesses, busy, onJoin, onStart } = props;
    const names = joinableBusinesses.map(p =>
        businessDisplayName(toBusinessProfileDetails(p)),
    );

    return (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 space-y-3">
            <div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900">{officeName} office</span>
                    <Badge value="Not started" type="info" />
                </div>
                <p className="text-sm text-gray-700 mt-2 max-w-2xl">
                    This office isn&apos;t covered by a business yet, so its phone lines
                    can&apos;t be registered.{" "}
                    {names.length > 0
                        ? `Carriers verify one business per EIN. Is ${officeName} part of ${names.join(" or ")}, or does it have its own EIN?`
                        : "Carriers verify one business per EIN. Start with the business this office operates under."}
                </p>
            </div>
            <div className="flex flex-wrap gap-2">
                {joinableBusinesses.map((profile, index) => (
                    <SecondaryButton
                        key={profile.id}
                        type="button"
                        disabled={busy}
                        onClick={() => onJoin(profile)}>
                        It&apos;s part of {names[index]}
                    </SecondaryButton>
                ))}
                {names.length > 0 ? (
                    <SecondaryButton type="button" disabled={busy} onClick={onStart}>
                        It has its own EIN
                    </SecondaryButton>
                ) : (
                    <PrimaryButton type="button" disabled={busy} onClick={onStart}>
                        Verify this office&apos;s business
                    </PrimaryButton>
                )}
            </div>
        </div>
    );
};
